/**
 * Customer Feedback — the MFX feedback pivot, and the records behind any bucket.
 *
 * Modelled on Sheet2 of the QA team's monthly export: Dept › Type › Category
 * down the side, Location across the top, a count in every cell. Every level
 * rolls up from the server's leaf rows (`pivot`), so a dept subtotal is the sum
 * of exactly the cells beneath it.
 *
 * **Drill-down is a filter, not a request.** Clicking a row label, a cell or a
 * location heading selects that bucket, and the records list below is `primary`
 * filtered on the same four label columns the pivot groups by. The server pins
 * that every bucket's count equals the records it lists, so the two cannot
 * disagree.
 *
 * Credit stays null where MFX recorded none: a cell with records but no credit
 * reads "—", never "$0.00".
 *
 * **Two views of the same cells.** "By location" puts the locations across the
 * top (Sheet2). "Trend" puts the window's months across the top for the
 * location picked in the filter bar. The leaf rows carry both the location and
 * the created month, so either view rolls up from the same cells, and a month
 * column lists exactly the records whose `created_month` it is.
 */

import { Fragment, type ReactNode, useMemo, useState } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { displayText, formatDate, monthLabel, summarize } from '../shared/format'
import type { Cell } from '../../lib/types'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

const BLANK = '(blank)'
const BY_LOCATION = 'by_location'
const TREND = 'trend'

type Measure = 'items' | 'credit'
type View = typeof BY_LOCATION | typeof TREND

/**
 * A bucket: any prefix of dept › type › category, optionally narrowed to one
 * column -- a location in the location view, a month in the trend.
 */
interface Selection {
  readonly dept: string | null
  readonly type: string | null
  readonly categoryKey: string | null
  readonly location: string | null
  readonly month: string | null
}

const NOTHING: Selection = {
  dept: null,
  type: null,
  categoryKey: null,
  location: null,
  month: null,
}

interface Totals {
  items: number
  open: number
  credit: number | null
}

function emptyTotals(): Totals {
  return { items: 0, open: 0, credit: null }
}

function add(into: Totals, leaf: Keyed): void {
  into.items += count(leaf.items)
  into.open += count(leaf.open_items)
  const credit = leaf.credit
  if (typeof credit === 'number') into.credit = (into.credit ?? 0) + credit
}

interface Node {
  readonly key: string
  readonly label: string
  readonly total: Totals
  /** Totals per column: per location, or per month in the trend. */
  readonly byColumn: Map<string, Totals>
  readonly children: Map<string, Node>
}

function node(key: string, label: string): Node {
  return {
    key,
    label,
    total: emptyTotals(),
    byColumn: new Map(),
    children: new Map(),
  }
}

function accumulate(target: Node, column: string, leaf: Keyed): void {
  add(target.total, leaf)
  let cell = target.byColumn.get(column)
  if (cell === undefined) {
    cell = emptyTotals()
    target.byColumn.set(column, cell)
  }
  add(cell, leaf)
}

function child(parent: Node, key: string, label: string): Node {
  let found = parent.children.get(key)
  if (found === undefined) {
    found = node(key, label)
    parent.children.set(key, found)
  }
  return found
}

/** Excel's pivot order: alphabetical ignoring case, "(blank)" last. */
function byLabel(a: string, b: string): number {
  if (a === BLANK) return b === BLANK ? 0 : 1
  if (b === BLANK) return -1
  return a.localeCompare(b, undefined, { sensitivity: 'base' })
}

function sortedChildren(parent: Node): Node[] {
  return [...parent.children.values()].sort(
    (a, b) => byLabel(a.label, b.label) || a.key.localeCompare(b.key),
  )
}

function money(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  })
}

function moneyOrBlank(value: Cell | undefined): string {
  return typeof value === 'number' ? money(value) : '—'
}

function windowLabel(from: string | null, to: string | null): string {
  if (from === null || to === null) return ''
  return from === to ? monthLabel(from) : `${monthLabel(from)} – ${monthLabel(to)}`
}

function matches(row: Keyed, selection: Selection): boolean {
  if (selection.dept !== null && text(row.fb_dept) !== selection.dept) return false
  if (selection.type !== null && text(row.fb_type) !== selection.type) return false
  if (selection.categoryKey !== null && text(row.category_key) !== selection.categoryKey) {
    return false
  }
  if (selection.location !== null && text(row.location) !== selection.location) return false
  if (selection.month !== null && text(row.created_month) !== selection.month) return false
  return true
}

function isEmpty(selection: Selection): boolean {
  return (
    selection.dept === null &&
    selection.type === null &&
    selection.categoryKey === null &&
    selection.location === null &&
    selection.month === null
  )
}

function sameSelection(a: Selection, b: Selection): boolean {
  return (
    a.dept === b.dept &&
    a.type === b.type &&
    a.categoryKey === b.categoryKey &&
    a.location === b.location &&
    a.month === b.month
  )
}

/** A select parameter's value; a date range is not a select. */
function paramText(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span className={`chevron${open ? ' is-open' : ''}`} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    </span>
  )
}

export function CustomerFeedback({ result, values, setParam }: CustomReportProps) {
  const kpis = dataset(result, 'kpis')[0]
  const pivot = dataset(result, 'pivot')
  const records = dataset(result, 'primary')
  const windowMonths = dataset(result, 'window_months')

  // The server says which view it derived; the URL value is the fallback.
  const view: View =
    (text(kpis?.view) ?? paramText(values.view) ?? BY_LOCATION) === TREND ? TREND : BY_LOCATION
  const trend = view === TREND

  const [measure, setMeasure] = useState<Measure>('items')
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  // A selection belongs to the payload it was made on: a new view or filter is
  // a new set of buckets, so an old selection would silently list nothing.
  const token = `${view}|${JSON.stringify(result.params)}`
  const [picked, setPicked] = useState<{ token: string; selection: Selection }>({
    token,
    selection: NOTHING,
  })
  const selection = picked.token === token ? picked.selection : NOTHING
  const setSelection = (next: Selection | ((current: Selection) => Selection)) => {
    setPicked((current) => {
      const base = current.token === token ? current.selection : NOTHING
      return { token, selection: typeof next === 'function' ? next(base) : next }
    })
  }

  const { root, columns } = useMemo(() => {
    const top = node('', 'Grand Total')
    const seen = new Set<string>()
    for (const leaf of pivot) {
      const column = (trend ? text(leaf.created_month) : text(leaf.location)) ?? BLANK
      const dept = text(leaf.fb_dept) ?? BLANK
      const type = text(leaf.fb_type) ?? BLANK
      const categoryKey = text(leaf.category_key) ?? BLANK
      const category = text(leaf.category) ?? BLANK
      seen.add(column)
      accumulate(top, column, leaf)
      const deptNode = child(top, dept, dept)
      accumulate(deptNode, column, leaf)
      const typeNode = child(deptNode, type, type)
      accumulate(typeNode, column, leaf)
      accumulate(child(typeNode, categoryKey, category), column, leaf)
    }
    // The trend's columns are every month in the window, oldest first, so a
    // month with no feedback is a visible zero rather than a missing column.
    const months = windowMonths.map((row) => text(row.value) ?? '').filter(Boolean)
    return { root: top, columns: trend ? months : [...seen].sort(byLabel) }
  }, [pivot, trend, windowMonths])

  /** The selection a column narrows to, in the current view. */
  const columnPath = (base: Selection, column: string): Selection =>
    trend ? { ...base, month: column } : { ...base, location: column }
  const columnLabel = (column: string): string => (trend ? monthLabel(column) : column)

  const maxCell = useMemo(() => {
    let max = 0
    for (const dept of root.children.values()) {
      for (const type of dept.children.values()) {
        for (const category of type.children.values()) {
          for (const cell of category.byColumn.values()) max = Math.max(max, cell.items)
        }
      }
    }
    return max
  }, [root])

  const selected = useMemo(
    () => records.filter((row) => matches(row, selection)),
    [records, selection],
  )
  const selectedTotals = useMemo(() => {
    const totals = emptyTotals()
    for (const row of selected) {
      totals.items += 1
      if (text(row.open_state) === 'open') totals.open += 1
      const credit = row.customer_credit
      if (typeof credit === 'number') totals.credit = (totals.credit ?? 0) + credit
    }
    return totals
  }, [selected])

  const pick = (next: Selection) => {
    setSelection((current) => (sameSelection(current, next) ? NOTHING : next))
  }
  const toggle = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const collapseAll = () => {
    const keys = new Set<string>()
    for (const dept of root.children.values()) keys.add(dept.key)
    setCollapsed(keys)
  }

  const show = (totals: Totals | undefined): string => {
    if (totals === undefined || totals.items === 0) return ''
    return measure === 'items' ? totals.items.toLocaleString() : money(totals.credit)
  }
  const shade = (totals: Totals | undefined): string | undefined => {
    if (totals === undefined || totals.items === 0 || maxCell === 0) return undefined
    const pct = Math.round(8 + (totals.items / maxCell) * 52)
    return `color-mix(in srgb, var(--series-1) ${String(pct)}%, transparent)`
  }
  const tip = (totals: Totals | undefined): string | undefined => {
    if (totals === undefined || totals.items === 0) return undefined
    return `${totals.items.toLocaleString()} records · ${totals.open.toLocaleString()} open · credit ${money(totals.credit)}`
  }

  const renderRow = (target: Node, level: 0 | 1 | 2, path: Selection, rowKey: string) => {
    const hasChildren = level < 2 && target.children.size > 0
    const isCollapsed = collapsed.has(rowKey)
    const rowPath = { ...path, location: null, month: null }
    const rowSelected = sameSelection(selection, rowPath) && !isEmpty(selection)
    return (
      <tr key={rowKey} className={`cf-row cf-level-${String(level)}`}>
        <th scope="row" className="cf-label-cell">
          <div className="cf-label" style={{ paddingLeft: `${String(level * 18)}px` }}>
            {hasChildren ? (
              <button
                type="button"
                className="chevron"
                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${target.label}`}
                aria-expanded={!isCollapsed}
                onClick={() => {
                  toggle(rowKey)
                }}
              >
                <Chevron open={!isCollapsed} />
              </button>
            ) : (
              <span className="cf-chevron-space" />
            )}
            <button
              type="button"
              className={`cf-label-button${rowSelected ? ' is-selected' : ''}`}
              aria-pressed={rowSelected}
              onClick={() => {
                pick(rowPath)
              }}
              title={target.label}
            >
              {target.label}
            </button>
          </div>
        </th>
        {columns.map((column) => {
          const cell = target.byColumn.get(column)
          const cellPath = columnPath(path, column)
          const isSelected = sameSelection(selection, cellPath)
          const value = show(cell)
          return (
            <td key={column} className="cf-cell" style={{ background: shade(cell) }}>
              {value !== '' && (
                <button
                  type="button"
                  className={`cf-cell-button${isSelected ? ' is-selected' : ''}`}
                  aria-pressed={isSelected}
                  aria-label={`${target.label}, ${columnLabel(column)}: ${value}`}
                  title={tip(cell)}
                  onClick={() => {
                    pick(cellPath)
                  }}
                >
                  {value}
                </button>
              )}
            </td>
          )
        })}
        <td className="cf-cell cf-total-cell" title={tip(target.total)}>
          {show(target.total)}
        </td>
      </tr>
    )
  }

  const rowsOut: ReactNode[] = []
  for (const dept of sortedChildren(root)) {
    const deptPath: Selection = { ...NOTHING, dept: dept.key }
    rowsOut.push(renderRow(dept, 0, deptPath, dept.key))
    if (collapsed.has(dept.key)) continue
    for (const type of sortedChildren(dept)) {
      const typeKey = `${dept.key}\u0000${type.key}`
      const typePath: Selection = { ...deptPath, type: type.key }
      rowsOut.push(renderRow(type, 1, typePath, typeKey))
      if (collapsed.has(typeKey)) continue
      for (const category of sortedChildren(type)) {
        rowsOut.push(
          renderRow(
            category,
            2,
            { ...typePath, categoryKey: category.key },
            `${typeKey}\u0000${category.key}`,
          ),
        )
      }
    }
  }

  const fromMonth = kpis === undefined ? null : text(kpis.from_month)
  const toMonth = kpis === undefined ? null : text(kpis.to_month)
  const medianOpen = kpis?.median_days_open
  const creditTotal = kpis?.credit_total
  const location = paramText(values.location)
  const trendOf = location === null || location === '(all)' ? 'all locations' : location

  return (
    <div className="bespoke-report cf">
      <section className="cf-kpis" aria-label="Summary">
        <div className="cf-kpi">
          <span className="cf-kpi-label">Records · {windowLabel(fromMonth, toMonth)}</span>
          <span className="cf-kpi-value">{count(kpis?.items).toLocaleString()}</span>
        </div>
        <div className="cf-kpi">
          <span className="cf-kpi-label">Open</span>
          <span className="cf-kpi-value">{count(kpis?.open_items).toLocaleString()}</span>
        </div>
        <div className="cf-kpi">
          <span className="cf-kpi-label">Customer credit</span>
          <span className="cf-kpi-value">{moneyOrBlank(creditTotal)}</span>
          <span className="cf-kpi-detail">
            on {count(kpis?.credited_items).toLocaleString()} records; blank credit is not $0
          </span>
        </div>
        <div className="cf-kpi">
          <span className="cf-kpi-label">Median days open</span>
          <span className="cf-kpi-value">
            {typeof medianOpen === 'number' ? medianOpen.toLocaleString() : '—'}
          </span>
          <span className="cf-kpi-detail">open records, as of today</span>
        </div>
      </section>

      <Card
        title={
          trend
            ? `Feedback by month · ${trendOf}`
            : 'Feedback by department, type and category'
        }
        subtitle={
          trend
            ? `${windowLabel(fromMonth, toMonth)}, oldest first. Pick a location in the filter bar to trend one site. Click a row, a cell or a month heading to list its records below. Shading follows the record count.`
            : 'Click a row, a cell or a location heading to list its records below. Shading follows the record count.'
        }
        actions={
          <div className="cf-actions">
            <div className="cf-toggle" role="group" aria-label="View">
              <button
                type="button"
                aria-pressed={!trend}
                className={!trend ? 'is-active' : ''}
                onClick={() => {
                  setParam('view', BY_LOCATION)
                }}
              >
                By location
              </button>
              <button
                type="button"
                aria-pressed={trend}
                className={trend ? 'is-active' : ''}
                onClick={() => {
                  setParam('view', TREND)
                }}
              >
                Trend
              </button>
            </div>
            <div className="cf-toggle" role="group" aria-label="Measure">
              <button
                type="button"
                aria-pressed={measure === 'items'}
                className={measure === 'items' ? 'is-active' : ''}
                onClick={() => {
                  setMeasure('items')
                }}
              >
                Count
              </button>
              <button
                type="button"
                aria-pressed={measure === 'credit'}
                className={measure === 'credit' ? 'is-active' : ''}
                onClick={() => {
                  setMeasure('credit')
                }}
              >
                Credit
              </button>
            </div>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setCollapsed(new Set())
              }}
            >
              Expand all
            </button>
            <button type="button" className="link-button" onClick={collapseAll}>
              Collapse all
            </button>
          </div>
        }
      >
        {pivot.length === 0 ? (
          <Empty label="No feedback recorded for these filters." />
        ) : (
          <div className="cf-matrix-scroll">
            <table className="cf-matrix">
              <thead>
                <tr>
                  <th scope="col" className="cf-label-head">
                    Dept › Type › Category
                  </th>
                  {columns.map((column) => {
                    const path = columnPath(NOTHING, column)
                    const isSelected = sameSelection(selection, path)
                    return (
                      <th scope="col" key={column}>
                        <button
                          type="button"
                          className={`cf-head-button${isSelected ? ' is-selected' : ''}`}
                          aria-pressed={isSelected}
                          onClick={() => {
                            pick(path)
                          }}
                        >
                          {columnLabel(column)}
                        </button>
                      </th>
                    )
                  })}
                  <th scope="col">{trend ? 'Total' : 'Grand Total'}</th>
                </tr>
              </thead>
              <tbody>{rowsOut}</tbody>
              <tfoot>
                <tr className="cf-row cf-grand">
                  <th scope="row" className="cf-label-cell">
                    <span className="cf-grand-label">Grand Total</span>
                  </th>
                  {columns.map((column) => (
                    <td key={column} className="cf-cell cf-total-cell">
                      {show(root.byColumn.get(column))}
                    </td>
                  ))}
                  <td className="cf-cell cf-total-cell">{show(root.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <Card
        title={isEmpty(selection) ? 'All records in view' : 'Records in this bucket'}
        subtitle={
          isEmpty(selection)
            ? 'Every record behind the matrix, newest first. Select a bucket above to narrow it.'
            : undefined
        }
        actions={
          isEmpty(selection) ? undefined : (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setSelection(NOTHING)
              }}
            >
              Clear selection
            </button>
          )
        }
      >
        <p className="cf-bucket" aria-live="polite">
          {!isEmpty(selection) && (
            <span className="cf-bucket-path">{bucketPath(selection, root)}</span>
          )}
          <span className="cf-bucket-figures">
            {selectedTotals.items.toLocaleString()}{' '}
            {selectedTotals.items === 1 ? 'record' : 'records'} ·{' '}
            {selectedTotals.open.toLocaleString()} open · credit {money(selectedTotals.credit)}
          </span>
        </p>
        {selected.length === 0 ? (
          <Empty label="No records in this bucket." />
        ) : (
          <RecordTable rows={selected} />
        )}
      </Card>
    </div>
  )
}

function bucketPath(selection: Selection, root: Node): string {
  const parts: string[] = []
  const dept = selection.dept === null ? undefined : root.children.get(selection.dept)
  if (dept !== undefined) parts.push(dept.label)
  const type = selection.type === null ? undefined : dept?.children.get(selection.type)
  if (type !== undefined) parts.push(type.label)
  const category =
    selection.categoryKey === null ? undefined : type?.children.get(selection.categoryKey)
  if (category !== undefined) parts.push(category.label)
  const where = parts.join(' › ')
  const column =
    selection.month !== null
      ? monthLabel(selection.month)
      : selection.location !== null
        ? selection.location
        : null
  if (column === null) return where
  return where === '' ? column : `${where} · ${column}`
}

function RecordTable({ rows }: { rows: Keyed[] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) => {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  return (
    <div className="cf-records-scroll">
      <table className="detail-table cf-records">
        <colgroup>
          <col style={{ width: 30 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 300 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 110 }} />
        </colgroup>
        <thead>
          <tr>
            <th aria-label="Expand" />
            <th className="cell-number">Id</th>
            <th>Created</th>
            <th>Subject</th>
            <th>Category</th>
            <th>Location</th>
            <th>Status</th>
            <th>Assigned to</th>
            <th className="cell-number">Credit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = String(row.feedback_id ?? '')
            const isOpen = open.has(id)
            const days = row.days_open
            const status = displayText(text(row.fb_status))
            return (
              <Fragment key={id}>
                <tr className={`detail-row${isOpen ? ' is-open' : ''}`}>
                  <td className="chevron-cell">
                    <button
                      type="button"
                      className="chevron"
                      aria-label={`${isOpen ? 'Hide' : 'Show'} details for feedback ${id}`}
                      aria-expanded={isOpen}
                      onClick={() => {
                        toggle(id)
                      }}
                    >
                      <Chevron open={isOpen} />
                    </button>
                  </td>
                  <td className="cell-number">{id}</td>
                  <td className="cell-mono">{formatDate(text(row.created_at))}</td>
                  <td className="cell-truncate" title={displayText(text(row.subject))}>
                    {displayText(text(row.subject))}
                  </td>
                  <td className="cell-truncate" title={displayText(text(row.category))}>
                    {displayText(text(row.category))}
                  </td>
                  <td className="cell-truncate">{displayText(text(row.location))}</td>
                  <td>
                    <span
                      className={`cf-status${text(row.open_state) === 'open' ? ' is-open' : ''}`}
                    >
                      {status}
                    </span>
                    {typeof days === 'number' && (
                      <span className="cf-days"> · {days.toLocaleString()}d open</span>
                    )}
                  </td>
                  <td className="cell-truncate">{displayText(text(row.assigned_to))}</td>
                  <td className="cell-number">{moneyOrBlank(row.customer_credit)}</td>
                </tr>
                {isOpen && (
                  <tr className="cf-detail">
                    <td />
                    <td colSpan={8}>
                      <RecordDetail row={row} />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (value === null) return null
  return (
    <div className="cf-field">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function RecordDetail({ row }: { row: Keyed }) {
  const original = text(row.original_feedback)
  const resolution = text(row.resolution)
  const note = text(row.last_note)
  const cost = row.corrective_action_cost
  return (
    <div className="cf-record">
      <div className="cf-record-text">
        <h4>Original feedback</h4>
        <p>{original ?? 'None recorded.'}</p>
        {resolution !== null && (
          <>
            <h4>Resolution</h4>
            <p>{resolution}</p>
          </>
        )}
        {note !== null && (
          <>
            <h4>Latest note</h4>
            <p>{summarize(note, 600)}</p>
          </>
        )}
      </div>
      <dl className="cf-record-facts">
        <Field
          label="Dept › Type"
          value={`${displayText(text(row.fb_dept))} › ${displayText(text(row.fb_type))}`}
        />
        <Field label="Root cause" value={text(row.root_cause)} />
        <Field label="Owner" value={text(row.owner)} />
        <Field label="Customer ref" value={text(row.customer_ref)} />
        <Field label="Orders" value={text(row.order_nums)} />
        <Field label="Ship-to" value={joinRef(text(row.ship_to_nums), text(row.ship_to_names))} />
        <Field label="Products" value={joinRef(text(row.product_nums), text(row.product_names))} />
        <Field label="Lots" value={text(row.lot_nums)} />
        <Field
          label="Corrective action cost"
          value={typeof cost === 'number' ? money(cost) : null}
        />
        <Field label="Last modified" value={formatDate(text(row.modified_at)) || null} />
      </dl>
    </div>
  )
}

function joinRef(numbers: string | null, names: string | null): string | null {
  if (numbers === null) return names
  if (names === null) return numbers
  return `${numbers} — ${names}`
}
